import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * DuelResultPopup — popup affichée au lancement / retour de l'app quand un duel
 * s'est terminé pendant qu'on n'y était pas. Montre le SCORE directement (plus
 * besoin d'aller dans l'historique). Les duels terminés s'enchaînent un par un.
 *
 * props :
 *   result    : { themeName, themeIcon, myScore, theirScore, opponentName,
 *                 opponentAvatar, iWon (true|false|null), eloChange,
 *                 opponentId, themeId, duelId }
 *   remaining : nombre de duels encore dans la file (celui-ci inclus)
 *   onClose   : ferme la popup courante (→ affiche le suivant s'il y en a)
 *   onRematch : relance un défi (même adversaire, même thème)
 */
const TOTAL = 10; // DUEL_QUESTION_COUNT côté backend

export default function DuelResultPopup({ result, remaining, onClose, onRematch }) {
  if (!result) return null;
  const { iWon, myScore, theirScore, opponentName, themeName, themeIcon, eloChange } = result;

  const title = iWon === true ? '🏆 Victoire !' : iWon === false ? '🎯 Pas cette fois !' : '🤝 Égalité';
  const eloText = (eloChange || eloChange === 0)
    ? `${eloChange > 0 ? '+' : ''}${eloChange} ELO`
    : null;
  const more = (remaining || 1) - 1;

  return (
    <AnimatePresence>
      <motion.div
        className="duel-popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-modal="true"
      >
        <motion.div
          className="duel-popup-card duel-result-card"
          initial={{ y: 40, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', damping: 20, stiffness: 220 }}
        >
          <button className="duel-popup-close" onClick={onClose} aria-label="Fermer">×</button>
          <p className="duel-popup-kicker">Duel terminé · {themeIcon} {themeName}</p>
          <h2>{title}</h2>
          <div className="duel-result-versus">
            <div className="duel-result-player">
              <span className="duel-result-name">Toi</span>
              <span className="duel-result-score">{myScore ?? 0}<span className="duel-result-total">/{TOTAL}</span></span>
            </div>
            <span className="duel-result-vs">—</span>
            <div className="duel-result-player">
              <span className="duel-result-score">{theirScore ?? 0}<span className="duel-result-total">/{TOTAL}</span></span>
              <span className="duel-result-name">{opponentName}</span>
            </div>
          </div>
          {eloText && <p className={`duel-popup-elo ${eloChange > 0 ? 'up' : eloChange < 0 ? 'down' : ''}`}>{eloText}</p>}
          {iWon === false && <p className="duel-encourage">Beau combat ! Tu feras mieux la prochaine fois 💪</p>}

          <div className="duel-popup-actions">
            {result.opponentId && result.themeId && (
              <button className="btn-login" onClick={() => onRematch(result)}>🔁 Revanche</button>
            )}
            <button className="btn-login" onClick={onClose}>
              {more > 0 ? `Suivant → (${more})` : 'Fermer'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
